import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { MultiSelect } from 'primeng/multiselect';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ControlIdentity, CplaceMultiSelectControlChange, CplaceMultiSelectControlParameters, Option } from '../../../api/model/models';
import { CplaceControlService, CplaceControlWithEditModeComponent, CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR } from '../shared';

@Component({
  selector: 'cf-frontend-sdk-cplace-multi-select-control',
  templateUrl: './cplace-multi-select-control.component.html',
  styleUrls: ['./styles/cplace-multi-select-control.component.scss'],
  providers: [CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR(CplaceMultiSelectControlComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CplaceMultiSelectControlComponent extends CplaceControlWithEditModeComponent<string[]> {
  public static readonly componentName = 'CplaceMultiSelectControlComponent';
  public static readonly MAX_OPTIONS_WITHOUT_SEARCH_BAR = 8;
  public static readonly DROPDOWN_ICON = 'hui hui-caret-down';

  /**
   * Options shown in the dropdown to choose from.
   */
  @Input() public options: Option[] = [];

  /**
   * Whether the empty value view should be shown.
   */
  public $showEmptyValue: Observable<boolean> = combineLatest([this.$value, this.$editMode]).pipe(
    map(([value, editMode]) => {
      return (!value || value.length === 0) && !editMode;
    })
  );

  /**
   * Reference to the PrimeNG MultiSelect component.
   * @private
   */
  @ViewChild(MultiSelect, { static: true })
  private multiSelect!: MultiSelect;

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService
  ) {
    super(elementRef, changeDetectorRef, cplaceControlService);
  }

  /**
   * Defines whether or not the header of the {@link multiSelect} should be shown.
   */
  public showHeader(): boolean {
    return this.options.length > CplaceMultiSelectControlComponent.MAX_OPTIONS_WITHOUT_SEARCH_BAR;
  }

  /**
   * Gets the dropdown icon of the {@link multiSelect}.
   */
  public getDropdownIcon(): string {
    return this.multiSelect.overlayVisible ? CplaceMultiSelectControlComponent.DROPDOWN_ICON : '';
  }

  /**
   * Unfortunately the PrimeNG MultiSelect component loses focus when clicking on the select-all checkbox.
   * This method re-focuses the component to make sure that saving when clicking outside is still working.
   * @param event A {@link MouseEvent}
   */
  public handleOnClick(event: MouseEvent) {
    const multiSelectHeaderCheckbox = this.elementRef.nativeElement.querySelector('.p-multiselect-header .p-checkbox-box');
    if (multiSelectHeaderCheckbox?.contains(event.target as HTMLElement)) {
      this.elementRef.nativeElement.querySelector('input')?.focus();
    }
  }

  protected onClear(): void {
    this.value = [];
  }

  protected getControlChange(identity: ControlIdentity, value: string[]): CplaceMultiSelectControlChange {
    return { featureName: identity.featureName, componentName: CplaceMultiSelectControlComponent.componentName, value: value };
  }

  protected getComponentClassName(): string {
    return CplaceMultiSelectControlComponent.componentName;
  }

  protected initializeControlParameters(controlParameters: CplaceMultiSelectControlParameters): void {
    this.value = controlParameters.value;
    this.options = controlParameters.options;
  }
}
